import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faSpinner,
  faCheckCircle,
  faExclamationCircle,
  faArrowUp,
  faArrowDown,
  faMinus,
  faHistory
} from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../utils/apiConfig';
import './AutoPacingExecutionHistory.css';

/**
 * AutoPacingExecutionHistory - Modal to display execution history for an auto pacing schedule
 */
const AutoPacingExecutionHistory = ({ isOpen, onClose, schedule, campaign, accountId, user }) => {
  const [executions, setExecutions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ total: 0, hasMore: false });

  useEffect(() => {
    if (isOpen && schedule?.id) {
      fetchHistory();
    }
  }, [isOpen, schedule?.id]);

  const fetchHistory = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get(
        getApiUrl(`/api/v2/campaigns/${campaign.id}/auto-pacing/${schedule.id}/history`),
        { params: { accountId, userId: user?.userId || user?.id, limit: 50 } }
      );
      setExecutions(response.data.executions || []);
      setPagination(response.data.pagination || {});
    } catch (err) {
      console.error('Error fetching execution history:', err);
      setError('Failed to load execution history');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (startedAt, completedAt) => {
    if (!startedAt || !completedAt) return '-';
    const duration = new Date(completedAt) - new Date(startedAt);
    const seconds = Math.floor(duration / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const getStatusBadge = (status) => {
    const config = {
      completed: { icon: faCheckCircle, color: '#10b981', label: 'Completed' },
      running: { icon: faSpinner, color: '#6366f1', label: 'Running', spin: true },
      failed: { icon: faExclamationCircle, color: '#ef4444', label: 'Failed' },
      skipped: { icon: faMinus, color: '#9ca3af', label: 'Skipped' }
    };
    const { icon, color, label, spin } = config[status] || config.skipped;
    return (
      <span className="status-badge" style={{ color }}>
        <FontAwesomeIcon icon={icon} spin={spin} /> {label}
      </span>
    );
  };

  const getBudgetChangeIndicator = (changePercent) => {
    if (changePercent === null || changePercent === undefined) return null;
    const isPositive = changePercent > 0;
    const isNegative = changePercent < 0;
    return (
      <span className={`budget-change ${isPositive ? 'positive' : isNegative ? 'negative' : ''}`}>
        <FontAwesomeIcon icon={isPositive ? faArrowUp : isNegative ? faArrowDown : faMinus} />
        {Math.abs(changePercent).toFixed(1)}%
      </span>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="execution-history-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <FontAwesomeIcon icon={faHistory} /> Execution History
          </h2>
          <button className="close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="modal-subtitle">
          {schedule?.name} • {pagination.total} total executions
        </div>

        <div className="modal-body">
          {isLoading ? (
            <div className="loading-state">
              <FontAwesomeIcon icon={faSpinner} spin /> Loading history...
            </div>
          ) : error ? (
            <div className="error-state">{error}</div>
          ) : executions.length === 0 ? (
            <div className="empty-state">
              <FontAwesomeIcon icon={faHistory} size="2x" style={{ color: '#9ca3af', marginBottom: '12px' }} />
              <p>No executions yet</p>
            </div>
          ) : (
            <div className="executions-table-container">
              <table className="executions-table">
                <thead>
                  <tr>
                    <th>Started</th>
                    <th>Duration</th>
                    <th>Status</th>
                    <th>Budget Change</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {executions.map(exec => (
                    <tr key={exec.id} className={exec.status === 'failed' ? 'failed-row' : ''}>
                      <td>{formatDateTime(exec.startedAt)}</td>
                      <td>{formatDuration(exec.startedAt, exec.completedAt)}</td>
                      <td>{getStatusBadge(exec.status)}</td>
                      <td>
                        {exec.appliedBudget !== null ? (
                          <div className="budget-info">
                            <span>${exec.previousBudget?.toFixed(2) || '?'}</span>
                            <span className="arrow">→</span>
                            <span>${exec.appliedBudget?.toFixed(2)}</span>
                            {getBudgetChangeIndicator(exec.budgetChangePercent)}
                          </div>
                        ) : (
                          <span className="no-change">No change</span>
                        )}
                      </td>
                      <td>
                        {exec.errorMessage && (
                          <span className="error-message" title={exec.errorMessage}>
                            {exec.errorMessage.substring(0, 50)}...
                          </span>
                        )}
                        {!exec.errorMessage && exec.editStatus === 'skipped' && (
                          <span className="skipped-reason">No budget suggestion</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AutoPacingExecutionHistory;

